// Recursive client model for OEMS v2 trigger conditions. Each leaf is one
// condition family; `and`/`or` nodes hold child nodes (true nested trees).
// Fields use UI string tokens (e.g. source:'mark', op:'cross_above') that are
// mapped to proto enums by build-trigger-condition.ts. Persisted as JSON under
// OrderFormCollapsibleConfig.entryConditionTreeJson.

export type OperatorToken =
  | 'equal_to'
  | 'not_equal'
  | 'greater_than'
  | 'less_than'
  | 'greater_than_or_equal'
  | 'less_than_or_equal'
  | 'cross_above'
  | 'cross_below'

export type PriceSourceToken = 'mark' | 'last' | 'index' | 'mid' | 'best_bid' | 'best_ask'

export type IndicatorTypeToken = 'sma' | 'ema'

export type CandleFieldToken =
  | 'open'
  | 'high'
  | 'low'
  | 'close'
  | 'volume_base'
  | 'volume_quote'
  | 'vwap'
  | 'twap'

export type PortfolioMetricToken = 'account_value_usd' | 'currency_balance'
/** Currency balance read in the currency's own units, or marked to USD. */
export type PortfolioUnitToken = 'native' | 'usd'

export type PositionMetricToken =
  | 'quantity'
  | 'mark_price'
  | 'unrealized_pnl'
  | 'realized_pnl'
  | 'daily_unrealized_pnl'
  | 'initial_margin'
  | 'maintenance_margin'
  | 'delta'
  | 'gamma'
  | 'vega'
  | 'theta'
  | 'rho'
  | 'delta_usd'
  | 'gamma_usd'
  | 'vega_usd'
  | 'theta_usd'
  | 'unrealized_pnl_usd'
  | 'realized_pnl_usd'
  | 'daily_unrealized_pnl_usd'
  | 'initial_margin_usd'
  | 'maintenance_margin_usd'

export type RiskMetricToken =
  | 'pnl'
  | 'delta'
  | 'gamma'
  | 'vega'
  | 'theta'
  | 'rho'
  | 'vanna'
  | 'volga'
  | 'weighted_vega'

export type OptionIvSourceToken = 'bid_iv' | 'ask_iv' | 'mark_iv' | 'mid_iv'
export type OptionTypeToken = 'call' | 'put'
export type PriceOffsetUnitToken = 'quote_value' | 'bps' | 'percent' | 'ticks'
export type ScheduleVariantToken = 'fire_at' | 'delay'
export type PositionScopeKindToken = 'symbol' | 'underlying' | 'group' | 'all'
export type ActionEventTypeToken = 'placed' | 'filled' | 'partially_filled' | 'cancelled' | 'failed'
export type ActionEventTargetTypeToken = 'order_id' | 'algo_id' | 'symbol'

export interface IndicatorSpecValue {
  indicatorType: IndicatorTypeToken
  symbol: string
  source: PriceSourceToken
  periodCount: string
  timeframeSeconds: string
}

export interface RelativePriceValue {
  anchor: PriceSourceToken
  offset: string
  offsetUnit: PriceOffsetUnitToken
}

export type PriceNode = {
  kind: 'price'
  symbol: string
  source: PriceSourceToken
  op: OperatorToken
  value: string
  relative?: RelativePriceValue
}

export type TechnicalIndicatorNode = {
  kind: 'technical_indicator'
  left: IndicatorSpecValue
  op: OperatorToken
  targetMode: 'threshold' | 'indicator'
  threshold: string
  right: IndicatorSpecValue
}

export type ReturnsNode = {
  kind: 'returns'
  symbol: string
  source: PriceSourceToken
  windowMs: string
  op: OperatorToken
  valuePercent: string
  timeframeSeconds: string
}

export type CandleNode = {
  kind: 'candle'
  symbol: string
  timeframeSeconds: string
  field: CandleFieldToken
  op: OperatorToken
  value: string
  allowOpenCandle: boolean
}

export type PortfolioMetricNode = {
  kind: 'portfolio_metric'
  metric: PortfolioMetricToken
  /** Only read by `currency_balance`. */
  currency: string
  unit: PortfolioUnitToken
  op: OperatorToken
  value: string
  accountId?: string
}

export type PositionStatsNode = {
  kind: 'position_stats'
  scopeKind: PositionScopeKindToken
  scopeValue: string
  metric: PositionMetricToken
  op: OperatorToken
  value: string
  accountId?: string
}

export type RiskMetricNode = {
  kind: 'risk_metric'
  asset: string
  metric: RiskMetricToken
  op: OperatorToken
  value: string
  accountId?: string
}

export type OptionIvNode = {
  kind: 'option_iv'
  symbol: string
  underlying: string
  strikePrice?: string
  expiryMs?: string
  optionType?: OptionTypeToken
  source: OptionIvSourceToken
  op: OperatorToken
  valuePercent: string
}

export type ScheduleNode = {
  kind: 'schedule'
  variant: ScheduleVariantToken
  value: string
}

export type DbNewsNode = {
  kind: 'db_news'
  eeType: string[]
  newsType: string[]
  coins: string[]
  tags: string[]
  textContains: string[]
  eventNotBeforeMs?: string
}

export type ActionEventNode = {
  kind: 'action_event'
  eventType: ActionEventTypeToken
  targetType: ActionEventTargetTypeToken
  targetValue: string
  partialFillThreshold: string
}

export type AndNode = { kind: 'and'; children: ConditionNode[] }
export type OrNode = { kind: 'or'; children: ConditionNode[] }

export type LeafConditionNode =
  | PriceNode
  | TechnicalIndicatorNode
  | ReturnsNode
  | CandleNode
  | PortfolioMetricNode
  | PositionStatsNode
  | RiskMetricNode
  | OptionIvNode
  | ScheduleNode
  | DbNewsNode
  | ActionEventNode

export type GroupConditionNode = AndNode | OrNode

// `id` is a client-only stable handle for React keys (ignored by the builder and
// schema). It survives JSON round-trips so re-parsing config keeps keys stable.
export type ConditionNode = (LeafConditionNode | GroupConditionNode) & { id?: string }

export type ConditionKind = (LeafConditionNode | GroupConditionNode)['kind']

/** Default per-family timeframe (1h) and candle timeframe (5m), in seconds. */
const DEFAULT_TIMEFRAME = '3600'
const DEFAULT_CANDLE_TIMEFRAME = '300'

export interface ConditionNodeDefaults {
  symbol?: string
  accountId?: string
}

function defaultIndicatorSpec(periodCount: string, symbol = ''): IndicatorSpecValue {
  return {
    indicatorType: 'sma',
    symbol,
    source: 'mark',
    periodCount,
    timeframeSeconds: DEFAULT_TIMEFRAME,
  }
}

/**
 * Create a default node for a given kind. Leaves start with sensible enum
 * selections and blank numeric fields; groups seed two empty price children so
 * they satisfy the >=2-children rule immediately. db_news defaults
 * eventNotBeforeMs to creation time (`now`) so old/replayed posts can't satisfy
 * a freshly armed trigger.
 */
function defaultNode(
  kind: ConditionKind,
  now: number,
  defaults: ConditionNodeDefaults
): LeafConditionNode | GroupConditionNode {
  const symbol = defaults.symbol ?? ''
  const accountId = defaults.accountId

  switch (kind) {
    case 'price':
      return { kind, symbol, source: 'mark', op: 'greater_than_or_equal', value: '' }
    case 'technical_indicator':
      return {
        kind,
        left: defaultIndicatorSpec('10', symbol),
        op: 'cross_above',
        targetMode: 'indicator',
        threshold: '',
        right: defaultIndicatorSpec('50', symbol),
      }
    case 'returns':
      return {
        kind,
        symbol,
        source: 'mark',
        windowMs: '3600000',
        op: 'less_than_or_equal',
        valuePercent: '',
        timeframeSeconds: '60',
      }
    case 'candle':
      return {
        kind,
        symbol,
        timeframeSeconds: DEFAULT_CANDLE_TIMEFRAME,
        field: 'close',
        op: 'greater_than',
        value: '',
        allowOpenCandle: false,
      }
    case 'portfolio_metric':
      return {
        kind,
        metric: 'account_value_usd',
        currency: '',
        unit: 'native',
        op: 'less_than',
        value: '',
        accountId,
      }
    case 'position_stats':
      return {
        kind,
        scopeKind: 'symbol',
        scopeValue: symbol,
        metric: 'unrealized_pnl_usd',
        op: 'less_than',
        value: '',
        accountId,
      }
    case 'risk_metric':
      return { kind, asset: '', metric: 'delta', op: 'greater_than', value: '' }
    case 'option_iv':
      return {
        kind,
        symbol,
        underlying: '',
        source: 'mark_iv',
        op: 'greater_than',
        valuePercent: '',
      }
    case 'schedule':
      return { kind, variant: 'delay', value: '' }
    case 'db_news':
      return {
        kind,
        eeType: [],
        newsType: [],
        coins: [],
        tags: [],
        textContains: [],
        eventNotBeforeMs: String(now),
      }
    case 'action_event':
      return {
        kind,
        eventType: 'filled',
        targetType: 'order_id',
        targetValue: '',
        partialFillThreshold: '',
      }
    case 'and':
    case 'or':
      return {
        kind,
        children: [createNode('price', now, defaults), createNode('price', now, defaults)],
      }
  }
}

/** Generate a stable client id for a node (React keys only; ignored by builder/schema). */
export function newNodeId(): string {
  return globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
}

/** Create a default node for `kind`, tagged with a stable client id for React keys. */
export function createNode(
  kind: ConditionKind,
  now: number = Date.now(),
  defaults: ConditionNodeDefaults = {}
): ConditionNode {
  return { ...defaultNode(kind, now, defaults), id: newNodeId() }
}
